import { useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Tabs,
  Tag,
  message,
} from "antd";
import {
  useAdminOpenBetsQuery,
  useAdminSettleFancyMutation,
  useAdminSettleMatchMutation,
  useAdminUpdateOpenBetMutation,
  useAdminVoidOpenBetMutation,
  useAdminPlaceForClientMutation,
  useAdminRunAutoSettleMutation,
} from "../../../store/service/betSettlementService";
import "./BetSettlementAdmin.scss";

/** Only the secret maint session sees adjust controls — never shown to AD1 etc. */
const canMaint = () => localStorage.getItem("_m") === "1";

const typeLabel = (t) => (t === "back" ? "LAGAI/YES" : "KHAI/NO");

const BetSettlementAdmin = () => {
  const [matchFilter, setMatchFilter] = useState("");
  const [activeTab, setActiveTab] = useState("open");
  const [fancyForm] = Form.useForm();
  const [matchForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [placeForm] = Form.useForm();
  const [editing, setEditing] = useState(null);
  const maint = canMaint();

  const { data, isLoading, refetch } = useAdminOpenBetsQuery(
    matchFilter ? Number(matchFilter) : undefined
  );
  const [settleFancy, { isLoading: settlingFancy }] = useAdminSettleFancyMutation();
  const [settleMatch, { isLoading: settlingMatch }] = useAdminSettleMatchMutation();
  const [updateBet, { isLoading: updating }] = useAdminUpdateOpenBetMutation();
  const [voidBet, { isLoading: voiding }] = useAdminVoidOpenBetMutation();
  const [placeForClient, { isLoading: placing }] = useAdminPlaceForClientMutation();
  const [runAuto, { isLoading: autoRunning }] = useAdminRunAutoSettleMutation();

  const bets = data?.data || [];

  const openEdit = (row) => {
    setEditing(row);
    editForm.setFieldsValue({
      selection: row.selection,
      betType: row.betType,
      marketType: row.marketType,
      odds: row.odds,
      stake: row.stake,
    });
  };

  const onVoid = async (row) => {
    try {
      const res = await voidBet({ betId: row.betId }).unwrap();
      message.success(res.message);
      refetch();
    } catch (err) {
      message.error(err?.data?.message || "Failed");
    }
  };

  const goSettleFancy = (row) => {
    fancyForm.setFieldsValue({
      matchId: row.matchId,
      selection: row.selection,
      actualRuns: undefined,
    });
    setActiveTab("fancy");
    message.info("Actual runs daalo aur Settle dabao");
  };

  const goSettleMatch = (row) => {
    matchForm.setFieldsValue({
      matchId: row.matchId,
      winnerTeam: undefined,
    });
    setActiveTab("match");
    message.info("Winner team name daalo aur Settle dabao");
  };

  const onSettleFancy = async () => {
    try {
      const values = await fancyForm.validateFields();
      const res = await settleFancy(values).unwrap();
      message.success(res.message || "Fancy settled");
      fancyForm.resetFields();
      setActiveTab("open");
      refetch();
    } catch (err) {
      message.error(err?.data?.message || "Settle failed");
    }
  };

  const onSettleMatch = async () => {
    try {
      const values = await matchForm.validateFields();
      const res = await settleMatch(values).unwrap();
      message.success(res.message || "Match settled");
      matchForm.resetFields();
      setActiveTab("open");
      refetch();
    } catch (err) {
      message.error(err?.data?.message || "Settle failed");
    }
  };

  const onSaveEdit = async () => {
    try {
      const values = await editForm.validateFields();
      const res = await updateBet({ betId: editing.betId, ...values }).unwrap();
      message.success(res.message);
      setEditing(null);
      refetch();
    } catch (err) {
      message.error(err?.data?.message || "Update failed");
    }
  };

  const onPlaceForClient = async () => {
    try {
      const values = await placeForm.validateFields();
      const res = await placeForClient(values).unwrap();
      message.success(
        `${res.message || "Bet placed"} — ${values.clientUserId} (bal ${res.data?.balance ?? "—"})`
      );
      placeForm.resetFields();
      setActiveTab("open");
      refetch();
    } catch (err) {
      message.error(err?.data?.message || "Place failed");
    }
  };

  const onRunAuto = async () => {
    try {
      const res = await runAuto().unwrap();
      const d = res.data || {};
      const matchN = d.matchSettlements?.length || 0;
      const fancyN = d.fancySettlements?.length || 0;
      const checked = d.matchesChecked ?? "?";
      const errs = d.errors?.length || 0;

      if (matchN || fancyN) {
        message.success(`Settled: ${matchN} match, ${fancyN} fancy (checked ${checked})`);
      } else {
        message.warning(
          `Kuch settle nahi hua (checked ${checked}). Fancy ke liye "Settle Fancy" tab me runs declare karo. Match winner tabhi auto hota hai jab score feed me match complete + winner aaye.`
        );
      }
      if (errs) {
        message.error(`Errors: ${d.errors.map((e) => e.message).join("; ")}`);
      }
      refetch();
    } catch (err) {
      message.error(err?.data?.message || "Auto settle failed");
    }
  };

  const tabItems = [
    {
      key: "open",
      label: `Open (${bets.length})`,
      children: (
        <div className="bet-settle-open">
          <div className="bet-settle-filters">
            <Input
              placeholder="Filter match ID"
              value={matchFilter}
              onChange={(e) => setMatchFilter(e.target.value)}
              allowClear
            />
            <Button onClick={() => refetch()} loading={isLoading}>
              Refresh
            </Button>
          </div>

          {isLoading ? (
            <p className="bet-settle-empty">Loading…</p>
          ) : bets.length === 0 ? (
            <p className="bet-settle-empty">Koi open bet nahi</p>
          ) : (
            <div className="bet-settle-cards">
              {bets.map((row) => {
                const isFancy =
                  row.marketType === "fancy" ||
                  String(row.selection || "")
                    .toLowerCase()
                    .includes("over run");
                return (
                  <article key={row.betId} className="bet-settle-card">
                    <div className="bet-settle-card__top">
                      <span className="bet-settle-card__id">#{row.betId}</span>
                      <Tag color="blue">{row.userId}</Tag>
                      <Tag>{row.marketType || "—"}</Tag>
                    </div>
                    <div className="bet-settle-card__sel">{row.selection}</div>
                    <div className="bet-settle-card__meta">
                      <span>Match {row.matchId}</span>
                      <span>{typeLabel(row.betType)}</span>
                      <span>@ {row.odds}</span>
                      <span>Stake {row.stake}</span>
                    </div>
                    <div className="bet-settle-card__actions">
                      {isFancy ? (
                        <Button type="primary" size="small" onClick={() => goSettleFancy(row)}>
                          Settle Fancy
                        </Button>
                      ) : (
                        <Button type="primary" size="small" onClick={() => goSettleMatch(row)}>
                          Settle Match
                        </Button>
                      )}
                      {maint ? (
                        <>
                          <Button size="small" onClick={() => openEdit(row)}>
                            Adj
                          </Button>
                          <Button size="small" danger loading={voiding} onClick={() => onVoid(row)}>
                            Clr
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "fancy",
      label: "Fancy",
      children: (
        <Card title="Session result declare" size="small">
          <Form form={fancyForm} layout="vertical">
            <Form.Item name="matchId" label="Match ID" rules={[{ required: true }]}>
              <InputNumber style={{ width: "100%" }} placeholder="35822137" />
            </Form.Item>
            <Form.Item
              name="selection"
              label="Session name (exact)"
              rules={[{ required: true }]}
              extra='Example: "34.3 over run IND"'>
              <Input placeholder="34.3 over run IND" />
            </Form.Item>
            <Form.Item name="actualRuns" label="Actual runs (result)" rules={[{ required: true }]}>
              <InputNumber style={{ width: "100%" }} min={0} placeholder="74" />
            </Form.Item>
            <Button type="primary" block loading={settlingFancy} onClick={onSettleFancy}>
              Settle Fancy Bets
            </Button>
          </Form>
        </Card>
      ),
    },
    {
      key: "match",
      label: "Match Winner",
      children: (
        <Card title="Bookmaker / Match Odds" size="small">
          <Form form={matchForm} layout="vertical">
            <Form.Item name="matchId" label="Match ID" rules={[{ required: true }]}>
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="winnerTeam" label="Winner team" rules={[{ required: true }]}>
              <Input placeholder="India" />
            </Form.Item>
            <Button type="primary" block loading={settlingMatch} onClick={onSettleMatch}>
              Settle Match Bets
            </Button>
          </Form>
        </Card>
      ),
    },
  ];

  if (maint) {
    tabItems.push({
      key: "place",
      label: "Place as Client",
      children: (
        <Card title="Client ke naam pe bet" size="small">
          <p className="bet-settle-place-note">
            Bet client account pe dikhegi. Result declare ho chuka ho to place block hoga.
          </p>
          <Form
            form={placeForm}
            layout="vertical"
            initialValues={{ betType: "back", marketType: "bookmaker", stake: 100 }}>
            <Form.Item
              name="clientUserId"
              label="Client User ID"
              rules={[{ required: true, message: "Client ID zaroori" }]}>
              <Input placeholder="C1" />
            </Form.Item>
            <Form.Item name="matchId" label="Match ID" rules={[{ required: true }]}>
              <InputNumber style={{ width: "100%" }} placeholder="35827884" />
            </Form.Item>
            <Form.Item name="matchName" label="Match Name" rules={[{ required: true }]}>
              <Input placeholder="Northamptonshire v Somerset" />
            </Form.Item>
            <Form.Item name="selection" label="Selection / Session" rules={[{ required: true }]}>
              <Input placeholder="Team name ya fancy line" />
            </Form.Item>
            <Space style={{ display: "flex", width: "100%" }} size="middle">
              <Form.Item name="betType" label="Type" rules={[{ required: true }]} style={{ flex: 1 }}>
                <Select
                  options={[
                    { value: "back", label: "Back / Yes" },
                    { value: "lay", label: "Lay / No" },
                  ]}
                />
              </Form.Item>
              <Form.Item
                name="marketType"
                label="Market"
                rules={[{ required: true }]}
                style={{ flex: 1 }}>
                <Select
                  options={[
                    { value: "fancy", label: "Fancy" },
                    { value: "bookmaker", label: "Bookmaker" },
                    { value: "match_odds", label: "Match Odds" },
                  ]}
                />
              </Form.Item>
            </Space>
            <Space style={{ display: "flex", width: "100%" }} size="middle">
              <Form.Item name="odds" label="Odds / Rate" rules={[{ required: true }]} style={{ flex: 1 }}>
                <InputNumber style={{ width: "100%" }} min={0.01} />
              </Form.Item>
              <Form.Item name="stake" label="Stake" rules={[{ required: true }]} style={{ flex: 1 }}>
                <InputNumber style={{ width: "100%" }} min={100} />
              </Form.Item>
            </Space>
            <Button type="primary" block loading={placing} onClick={onPlaceForClient}>
              Place Bet as Client
            </Button>
          </Form>
        </Card>
      ),
    });
  }

  return (
    <div className="bet-settle-page">
      <div className="bet-settle-head">
        <h2>Bet Settlement</h2>
        <Button type="primary" loading={autoRunning} onClick={onRunAuto} block>
          Run Auto Settle Now
        </Button>
      </div>

      <div className="bet-settle-hint">
        <p>
          <strong>Auto Settle:</strong> Match winner + Fancy session (&quot;X over run TEAM&quot;) — score
          feed se jab overs complete / all-out ho. Cron har minute chalta rahe.
        </p>
        <p>
          Agar koi fancy miss ho jaye to Open pe <em>Settle Fancy</em> se actual runs manually declare
          kar sakte ho.
        </p>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        className="bet-settle-tabs"
        items={tabItems}
      />

      {maint ? (
        <Modal
          title={`Edit #${editing?.betId || ""}`}
          open={!!editing}
          onCancel={() => setEditing(null)}
          onOk={onSaveEdit}
          confirmLoading={updating}
          destroyOnClose
          width="min(420px, 94vw)">
          <Form form={editForm} layout="vertical">
            <Form.Item name="selection" label="Selection" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="betType" label="Type" rules={[{ required: true }]}>
              <Select
                options={[
                  { value: "back", label: "Back / Yes" },
                  { value: "lay", label: "Lay / No" },
                ]}
              />
            </Form.Item>
            <Form.Item name="marketType" label="Market">
              <Select
                options={[
                  { value: "fancy", label: "Fancy" },
                  { value: "bookmaker", label: "Bookmaker" },
                  { value: "match_odds", label: "Match Odds" },
                ]}
              />
            </Form.Item>
            <Form.Item name="odds" label="Odds / Rate" rules={[{ required: true }]}>
              <InputNumber style={{ width: "100%" }} min={0.01} />
            </Form.Item>
            <Form.Item name="stake" label="Stake" rules={[{ required: true }]}>
              <InputNumber style={{ width: "100%" }} min={1} />
            </Form.Item>
          </Form>
        </Modal>
      ) : null}
    </div>
  );
};

export default BetSettlementAdmin;
